import React, { useState, useMemo, useRef, useEffect } from "react";

export const getCafeIdentifier = (cafe) => {
  if (!cafe) {
    return "";
  }

  // Support both old API format (id, storeId, cafeId) and new Rista API format (ristaId, cafeCode)
  const rawId = cafe.id ?? cafe.storeId ?? cafe.cafeId ?? cafe.ristaId ?? cafe.cafeCode;
  return rawId !== undefined && rawId !== null ? String(rawId) : "";
};

export const getCafeDisplayName = (cafe, index) =>
  cafe?.name || cafe?.storeName || cafe?.cafeName || `Cafe ${index + 1}`;

const normalize = (value = "") => value.toLowerCase().trim();

export default function CafeDropdown({
  cafes = [],
  value,
  onChange,
  placeholder = "Select a café",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);

  const filteredCafes = useMemo(() => {
    const normalizedQuery = normalize(query);

    if (!normalizedQuery) {
      return cafes;
    }

    return cafes.filter((cafe, index) => {
      const name = getCafeDisplayName(cafe, index);
      const address = cafe?.address || cafe?.location || "";
      const combined = `${name} ${address}`;
      return normalize(combined).includes(normalizedQuery);
    });
  }, [cafes, query]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
    }
  }, [isOpen]);

  const selectedCafe = cafes.find((cafe) => getCafeIdentifier(cafe) === value);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        className="w-full h-12 rounded-2xl border border-slate-200 bg-white px-4 flex items-center justify-between text-base text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#10B3C6]/40 focus:border-[#10B3C6]"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className={selectedCafe ? "text-slate-700" : "text-slate-400"}>
          {selectedCafe ? getCafeDisplayName(selectedCafe, 0) : placeholder}
        </span>
        <svg
          className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 z-50 mt-3 rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
          <div className="border-b border-slate-100">
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="w-full h-12 px-4 text-base text-slate-700 focus:outline-none"
              placeholder="Search cafés"
              autoFocus
            />
          </div>

          <div className="max-h-64 overflow-y-auto">
            {filteredCafes.length === 0 ? (
              <div className="p-4 text-sm text-slate-500">No cafés found</div>
            ) : (
              filteredCafes.map((cafe, index) => {
                const rawId = getCafeIdentifier(cafe);
                const fallbackId = `fallback-${index}`;
                const id = rawId ? `cafe-${rawId}-${index}` : fallbackId;
                const name = getCafeDisplayName(cafe, index);
                const address = cafe?.address || cafe?.location;

                const isSelected = id === value;

                return (
                  <button
                    key={id}
                    type="button"
                    className={`w-full text-left px-4 py-3 text-sm transition hover:bg-[#10B3C6]/10 ${
                      isSelected ? "bg-[#10B3C6]/10 text-[#0e7f8f]" : "text-slate-700"
                    }`}
                    onClick={() => {
                      onChange?.(rawId || "");
                      setIsOpen(false);
                    }}
                  >
                    <div className="font-medium">{name}</div>
                    {address ? (
                      <div className="text-xs text-slate-500 mt-1">{address}</div>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
